import React from 'react';
import css from './ListingPage.module.css';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import Ratings from '../../components/Ratings/Ratings';

const ListingPageHeader = ({
  className,
  title,
  city,
  region,
  country,
  listingDistance,
  showInMiles,
  richTitle,
  rating,
  unitCount,
}) => {
  const intl = useIntl();
  const distance = showInMiles
    ? listingDistance?.miles
    : listingDistance?.kilometers;

  const locationLabel = [city, region || country].filter(Boolean).join(', ');

  // "View Map" used to do nothing on desktop: the map lives in the right-hand column,
  // which was position:sticky and therefore always already inside the viewport - so a
  // window-level scroll had nothing left to do. That column is plain static now
  // (.desktopColumn in ListingPage.module.css), so the map sits at a real page offset and
  // this scrolls to it. `block: 'start'` puts the "Item Location" heading at the top.
  const scrollToLocation = e => {
    e.preventDefault();
    if (typeof document === 'undefined') return;
    const locationElement = document.getElementById('location');
    if (!locationElement) return;
    locationElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header className={classNames(css.pillsContainer, className)}>
      <div className={css.pillsContainerContent}>
        <h1 className={css.title}>
          <FormattedMessage
            id="ListingPage.title"
            values={{ title: richTitle }}
          />
        </h1>

        <div className={css.ownerInitialInfo}>
          {/* Rating (single gold star + value) directly left of the location pin/text,
              shown on both mobile and desktop so the header is consistent across
              breakpoints. Reuses the shared Ratings component (variant="orderPanel" =
              single star + value, already used this way in AboutOwner.js) rather than
              building a new star icon. */}
          <div className={css.mobileRatingWrapper}>
            <Ratings
              rating={rating}
              variant="orderPanel"
              className={css.mobileRatingContent}
              valueClassName={css.mobileRatingValue}
            />
          </div>
          <div className={css.locationWrapper}>
            <i
              className="ri-map-pin-2-line"
              style={{ fontSize: '20px', color: '#101828' }}
              aria-hidden="true"
            ></i>

            <button
              type="button"
              aria-label={intl.formatMessage({ id: 'ListingPageHeader.scrollToLocation' })}
              className={classNames(css.locationInfo, css.locationInfoDesktop)}
              onClick={scrollToLocation}
            >
              {!!distance && distance != null && (
                <p className={css.locationText}>
                  <FormattedMessage
                    id={showInMiles ? 'ListingPageHeader.milesAway' : 'ListingPageHeader.kmAway'}
                    defaultMessage={showInMiles ? '{distance} miles away' : '{distance} km away'}
                    values={{ distance }}
                  />
                  {locationLabel ? ' - ' : null}
                </p>
              )}
              {locationLabel ? (
                <p className={css.locationText}>{locationLabel}</p>
              ) : null}
              <p className={classNames(css.locationText, css.viewMapLink)}>
                <FormattedMessage id="ListingPageHeader.viewMap" defaultMessage="View Map" />
              </p>
            </button>
            <button
              type="button"
              aria-label={intl.formatMessage({ id: 'ListingPageHeader.scrollToLocation' })}
              className={classNames(css.locationInfo, css.locationInfoMobile)}
              onClick={scrollToLocation}
            >
              {locationLabel ? (
                <p className={css.locationText}>
                  {locationLabel}
                  &nbsp;
                </p>
              ) : null}
              {!!distance && distance != null && (
                <p className={css.locationText}>
                  •&nbsp;
                  <FormattedMessage
                    id={showInMiles ? 'ListingPageHeader.milesAway' : 'ListingPageHeader.kmAway'}
                    defaultMessage={showInMiles ? '{distance} miles away' : '{distance} km away'}
                    values={{ distance }}
                  />
                </p>
              )}
            </button>
          </div>
          {/* Total units the owner has (publicData.quantity), for search engines and AI
              assistants rather than people: customers already see the figure in the order
              panel's quantity field, but that panel is not server-rendered and its dropdown
              is not readable text. This header IS in the initial HTML, so the count lives
              here — visually hidden, still in the DOM and in innerText, alongside the same
              figure in the Product JSON-LD. Total stock only; there is no reliable per-date
              availability to claim. */}
          {unitCount ? (
            <p className={css.unitCountForCrawlers} data-testid="listing-header-unit-count">
              <FormattedMessage id="ListingPageHeader.unitCount" values={{ count: unitCount }} />
            </p>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default ListingPageHeader;
